import { Injectable, signal, inject, computed, WritableSignal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { tap } from 'rxjs/operators';
import { ApiResponse } from '@app/types';
import { Observable } from 'rxjs';
import { StatusDefinition } from '@app/types/status.type';

export type StatusContext = 'surtimiento' | 'cobranza';

@Injectable({
  providedIn: 'root'
})
export class StatusTranslatorService {
  private http = inject(HttpClient);
  private readonly API_URL = environment.API_URL;

  private statuses: WritableSignal<StatusDefinition[]> = signal<StatusDefinition[]>([]);

  // Mapa de traducción por contexto: clave original/variación -> nombre a mostrar
  private surtimientoMap = computed(() => {
    const map = new Map<string, string>();
    for (const statusDef of this.statuses()) {
      map.set(statusDef.status.toUpperCase(), statusDef.nombreSurtimiento);
      for (const variation of statusDef.variations) {
        map.set(variation.toUpperCase(), statusDef.nombreSurtimiento);
      }
    }
    return map;
  });

  private cobranzaMap = computed(() => {
    const map = new Map<string, string>();
    for (const statusDef of this.statuses()) {
      const display = statusDef.nombreCobranza || statusDef.nombreSurtimiento;
      map.set(statusDef.status.toUpperCase(), display);
      for (const variation of statusDef.variations) {
        map.set(variation.toUpperCase(), display);
      }
    }
    return map;
  });

  // Definiciones por clave normalizada (status o variación)
  private definitionsMap = computed(() => {
    const map = new Map<string, StatusDefinition>();
    for (const statusDef of this.statuses()) {
      map.set(statusDef.status.toUpperCase(), statusDef);
      for (const variation of statusDef.variations) {
        map.set(variation.toUpperCase(), statusDef);
      }
    }
    return map;
  });

  constructor() {
    this.loadStatuses().subscribe();
  }

  private loadStatuses() {
    return this.http.get<ApiResponse<StatusDefinition>>(`${this.API_URL}/statuses`).pipe(
      tap(response => {
        this.statuses.set(response.data);
      })
    );
  }

  public refreshStatuses(): Observable<ApiResponse<StatusDefinition>> {
    return this.loadStatuses();
  }

  public getStatuses(): WritableSignal<StatusDefinition[]> {
    return this.statuses;
  }

  public createStatus(payload: Partial<StatusDefinition>[]): Observable<ApiResponse<StatusDefinition>> {
    return this.http.post<ApiResponse<StatusDefinition>>(`${this.API_URL}/statuses`, payload).pipe(
      tap(() => this.refreshStatuses().subscribe())
    );
  }

  public updateStatus(updates: { id: string; changes: Partial<StatusDefinition> }[]): Observable<ApiResponse<StatusDefinition>> {
    return this.http.patch<ApiResponse<StatusDefinition>>(`${this.API_URL}/statuses`, updates).pipe(
      tap(() => this.refreshStatuses().subscribe())
    );
  }

  public deleteStatus(ids: string[]): Observable<ApiResponse<any>> {
    return this.http.delete<ApiResponse<any>>(`${this.API_URL}/statuses`, { body: { ids } }).pipe(
      tap(() => this.refreshStatuses().subscribe())
    );
  }

  /**
   * Traduce una clave de estatus al nombre a mostrar.
   * @param statusKey Clave o variación del estatus
   * @param context 'surtimiento' (órdenes) o 'cobranza' (monitores/dashboards)
   */
  public translate(statusKey: string | undefined | null, context: StatusContext = 'surtimiento'): string {
    if (!statusKey) {
      return 'No definido';
    }
    const upperKey = statusKey.toUpperCase().replace(/_/g, ' ');
    const map = context === 'cobranza' ? this.cobranzaMap() : this.surtimientoMap();
    return map.get(upperKey) || statusKey;
  }

  /**
   * Devuelve la definición del catálogo para una clave de estatus (o variación).
   */
  public getDefinition(statusKey: string | undefined | null): StatusDefinition | undefined {
    if (!statusKey) return undefined;
    const upperKey = statusKey.toUpperCase().replace(/_/g, ' ');
    return this.definitionsMap().get(upperKey);
  }

  /**
   * Color del catálogo para un estatus (undefined si no está definido).
   */
  public getColor(statusKey: string | undefined | null): string | undefined {
    return this.getDefinition(statusKey)?.color;
  }

  /**
   * Grupo de dashboard del catálogo para un estatus (undefined si no está definido).
   */
  public getDashboardGroup(statusKey: string | undefined | null): string | undefined {
    return this.getDefinition(statusKey)?.dashboardGroup;
  }
}
